import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useTranslate } from '@tolgee/react';
import {
  getOverlayTemplate,
  listOverlayTemplateFolders,
  listOverlayTemplates,
  type OverlayTemplateDetailDto,
  type OverlayTemplateOrdering
} from '../../../../api/overlayTemplates';
import { LomavisOverlayTemplateLibrary } from '../../../../lomavisSharedUi/templates/pages/LomavisOverlayTemplateLibrary';
import type {
  OverlayTemplateFolder,
  OverlayTemplateSortValue,
  OverlayTemplateSummary
} from '../../../../lomavisSharedUi/types/overlayTemplates';

type Props = {
  show: boolean;
  locationUuid: string;
  onClose: () => void;
  onSelect: (template: OverlayTemplateDetailDto) => void;
};

const TEMPLATE_SELECTOR_MODAL_WIDTH = 'min(90vw, 1600px)';
const TEMPLATE_SELECTOR_BODY_HEIGHT = '75vh';
const TEMPLATE_LIBRARY_PAGE_SIZE = 100;
const TEMPLATE_SEARCH_DEBOUNCE_MS = 250;

const WideModalDialog = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<typeof Modal.Dialog>
>(
  // react-bootstrap provides the dialog props through `dialogAs`.
  // eslint-disable-next-line react/prop-types
  ({ style, ...props }, ref) => {
    const dialogStyle: React.CSSProperties & {
      '--bs-modal-width': string;
    } = {
      ...style,
      '--bs-modal-width': TEMPLATE_SELECTOR_MODAL_WIDTH
    };

    return <Modal.Dialog {...props} ref={ref} style={dialogStyle} />;
  }
);

WideModalDialog.displayName = 'WideModalDialog';

const orderingBySort: Record<OverlayTemplateSortValue, OverlayTemplateOrdering> = {
  newest: '-pk',
  oldest: 'pk',
  nameAsc: 'title',
  nameDesc: '-title'
};

const OverlayTemplateSelectorModal: React.FC<Props> = ({
  show,
  locationUuid,
  onClose,
  onSelect
}) => {
  const { t } = useTranslate();
  const [folders, setFolders] = useState<OverlayTemplateFolder[]>([]);
  const [templates, setTemplates] = useState<OverlayTemplateSummary[]>([]);
  const [showAccountLibrary, setShowAccountLibrary] = useState(false);
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [searchValue, setSearchValue] = useState('');
  const [sortValue, setSortValue] = useState<OverlayTemplateSortValue>('newest');
  const [isLoading, setIsLoading] = useState(false);
  const loadedScopeKeyRef = useRef<string | null>(null);
  const [isFolderLoading, setIsFolderLoading] = useState(false);
  const [selectingTemplateId, setSelectingTemplateId] = useState<string | null>(
    null
  );
  const [reloadKey, setReloadKey] = useState(0);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const scope = useMemo(
    () =>
      showAccountLibrary
        ? { kind: 'accountPicker' as const, locationUuid }
        : { kind: 'profile' as const, locationUuid },
    [locationUuid, showAccountLibrary]
  );
  const scopeKey = `${scope.kind}:${scope.locationUuid}`;

  useEffect(() => {
    if (!show) return;
    setIsFolderLoading(true);
    listOverlayTemplateFolders(scope)
      .then((response) =>
        setFolders(response.results.map((folder) => ({
          id: folder.uuid,
          name: folder.name
        })))
      )
      .catch(() =>
        setErrorMessage(
          t(
            'lomavisCreator.templateEditor.templatePicker.loadFailed',
            'Could not load template folders.'
          )
        )
      )
      .finally(() => setIsFolderLoading(false));
  }, [scope, show, t]);

  useEffect(() => {
    if (!show) return;
    const timeout = window.setTimeout(() => {
      const isInitialScopeLoad = loadedScopeKeyRef.current !== scopeKey;
      if (isInitialScopeLoad) setIsLoading(true);
      setErrorMessage(null);
      listOverlayTemplates({
        scope,
        folderUuid: selectedFolderId,
        search: searchValue.trim(),
        ordering: orderingBySort[sortValue],
        limit: TEMPLATE_LIBRARY_PAGE_SIZE
      })
        .then((response) => {
          setTemplates(
            response.results.map((template) => ({
              id: template.uuid,
              title: template.title,
              thumbnailUrl: template.thumbnail_url,
              aspectRatio: template.aspect_ratio,
              folderId: template.folder_uuid
            }))
          );
          loadedScopeKeyRef.current = scopeKey;
        })
        .catch(() =>
          setErrorMessage(
            t(
              'lomavisCreator.templateEditor.templatePicker.loadFailed',
              'Could not load templates. Please try again.'
            )
          )
        )
        .finally(() => {
          if (isInitialScopeLoad) setIsLoading(false);
        });
    }, searchValue ? TEMPLATE_SEARCH_DEBOUNCE_MS : 0);
    return () => window.clearTimeout(timeout);
  }, [
    reloadKey,
    scope,
    scopeKey,
    searchValue,
    selectedFolderId,
    show,
    sortValue,
    t
  ]);

  const selectTemplate = async (templateId: string) => {
    setSelectingTemplateId(templateId);
    try {
      const template = await getOverlayTemplate(templateId, scope);
      onSelect(template);
      onClose();
    } catch {
      setErrorMessage(
        t(
          'lomavisCreator.templateEditor.templatePicker.selectFailed',
          'Could not load the selected template. Please try again.'
        )
      );
    } finally {
      setSelectingTemplateId(null);
    }
  };

  return (
    <Modal
      centered
      show={show}
      onHide={onClose}
      size="xl"
      dialogAs={WideModalDialog}
    >
      <Modal.Header closeButton>
        <Modal.Title>
          {t(
            'lomavisCreator.templateEditor.templatePicker.title',
            'Choose a template'
          )}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body
        className="p-0 overflow-hidden"
        style={{ height: TEMPLATE_SELECTOR_BODY_HEIGHT }}
      >
        <LomavisOverlayTemplateLibrary
          mode={showAccountLibrary ? 'accountPicker' : 'profileGroup'}
          templates={templates}
          folders={folders}
          selectedFolderId={selectedFolderId}
          searchValue={searchValue}
          sortValue={sortValue}
          sortOptions={[
            {
              value: 'newest',
              label: t('overlayTemplates.sortNewest', 'Newest first')
            },
            {
              value: 'oldest',
              label: t('overlayTemplates.sortOldest', 'Oldest first')
            },
            {
              value: 'nameAsc',
              label: t('overlayTemplates.sortNameAsc', 'Name A–Z')
            },
            {
              value: 'nameDesc',
              label: t('overlayTemplates.sortNameDesc', 'Name Z–A')
            }
          ]}
          labels={{
            title: t('overlayTemplates.profile.title', 'Overlay templates'),
            description: t(
              'overlayTemplates.library.description',
              'Choose a template for your post.'
            ),
            back: t('common.back', 'Back'),
            createTemplate: t('overlayTemplates.createTemplate', 'Create template'),
            addFromAccountLibrary: t(
              'overlayTemplates.addFromAccountLibrary',
              'Add from account library'
            ),
            search: t('overlayTemplates.search', 'Search templates'),
            sort: t('overlayTemplates.sort', 'Sort templates'),
            emptyTitle: t('overlayTemplates.emptyTitle', 'No templates found'),
            emptyDescription: t('overlayTemplates.emptyDescription', 'Try a different folder or search.'),
            retry: t('common.retry', 'Retry'),
            loading: t('common.loading', 'Loading…'),
            templateCount: (count) =>
              count === 1
                ? t('overlayTemplates.templateCountOne', '1 template')
                : t('overlayTemplates.templateCount', '{count} templates', {
                    count
                  }),
            folderSidebar: {
              navigation: t('overlayTemplates.folderNavigation', 'Template folders'),
              title: t('overlayTemplates.folders', 'Folders'),
              allTemplates: t('overlayTemplates.allTemplates', 'All templates'),
              newFolder: t('overlayTemplates.createFolder', 'Create folder'),
              folderActions: t('overlayTemplates.folderActions', 'Folder actions'),
              renameFolder: t('overlayTemplates.renameFolder', 'Rename folder'),
              deleteFolder: t('overlayTemplates.deleteFolder', 'Delete folder')
            },
            card: {
              edit: t('common.edit', 'Edit'),
              changeFolder: t('overlayTemplates.changeFolder', 'Change folder'),
              duplicate: t('overlayTemplates.duplicate', 'Duplicate'),
              clone: t('overlayTemplates.cloneInMyLibrary', 'Clone in my library'),
              delete: t('common.delete', 'Delete'),
              startPost: selectingTemplateId
                ? t('common.loading', 'Loading…')
                : t('overlayTemplates.startPost', 'Use template'),
              thumbnailLoading: t('common.loading', 'Loading…'),
              thumbnailUnavailable: t('overlayTemplates.thumbnailUnavailable', 'Preview unavailable')
            }
          }}
          showPageHeading={false}
          showBackAction={showAccountLibrary}
          showAccountLibraryAction={!showAccountLibrary}
          isLoading={isLoading || selectingTemplateId !== null}
          isFolderLoading={isFolderLoading}
          errorMessage={errorMessage}
          onSearchChange={setSearchValue}
          onSortChange={setSortValue}
          onFolderSelect={setSelectedFolderId}
          onBack={() => {
            setSelectedFolderId(null);
            setSearchValue('');
            setShowAccountLibrary(false);
          }}
          onOpenAccountLibrary={() => {
            setSelectedFolderId(null);
            setSearchValue('');
            setShowAccountLibrary(true);
          }}
          onStartPost={(templateId) => {
            void selectTemplate(templateId);
          }}
          onRetry={() => setReloadKey((key) => key + 1)}
        />
      </Modal.Body>
    </Modal>
  );
};

export default OverlayTemplateSelectorModal;
